import React, {useEffect, useState} from 'react'
import {ButtonIcons} from '@/components/button-icons/button-icon'
import {EllipseButton} from './button/ellipse-button'

interface ScrollBlockProps {
	className?: string
	children: React.ReactNode
	id?: string
	isAutoScroll?: boolean
	scrollTimerSeconds?: number
	scrollSpeed?: number
}

export const ScrollBlock: React.FC<ScrollBlockProps> = ({
	className = '',
	children = null,
	id = 'auto-scroll-content',
	isAutoScroll = false,
	scrollTimerSeconds = 5,
	scrollSpeed = 5,
}) => {
	const [autoScroll, setAutoScroll] = useState(isAutoScroll)
	const [autoStop, setAutoStop] = useState(true)
	const [scrollTimerMilliSeconds, setScrollTimerMilliSeconds] = useState(
		scrollTimerSeconds * 1000
	)
	const [scrollTopPos, setScrollTopPos] = useState(0)
	const [isScrollDivInView, setIsScrollDivInView] = useState(false)

	const toggleScroll = (e: React.MouseEvent): void => {
		e.stopPropagation()
		setAutoScroll(!autoScroll)
	}

	const pauseScroll = (): void => {
		if (autoStop) setAutoScroll(false)
	}

	const overwriteScroll = (): void => {
		const scrollDiv = document.getElementById(id)
		if (!scrollDiv) return

		setScrollTopPos(scrollDiv.scrollTop)
		setAutoScroll(false)
	}

	useEffect(() => {
		const onScroll = (): void => {
			const scrollDiv = document.getElementById(id)

			if (!scrollDiv) return
			setIsScrollDivInView(
				window.innerHeight > scrollDiv.getBoundingClientRect().top + 200
			)
		}
		const scrollDiv = document.getElementById(id)
		if (!scrollDiv) return

		setIsScrollDivInView(
			window.innerHeight > scrollDiv.getBoundingClientRect().top + 200
		)
		window.addEventListener('scroll', onScroll)

		function scrollContent(): void {
			if (!scrollDiv) return
			const isEndOfScroll =
				scrollDiv.scrollTop >=
				scrollDiv.scrollHeight - scrollDiv.offsetHeight
			if (!isEndOfScroll) {
				setScrollTopPos(scrollTopPos + scrollSpeed)
				scrollDiv.scrollTo({top: scrollTopPos, behavior: 'smooth'})
			} else {
				setAutoScroll(false)
			}
			setScrollTimerMilliSeconds(scrollTimerMilliSeconds - 100)
		}

		let timeoutTimer: NodeJS.Timeout | null = null
		if (isScrollDivInView) {
			if (scrollTimerMilliSeconds > 0 && autoScroll) {
				timeoutTimer = setTimeout(scrollContent, 100)
			} else if (!autoStop && autoScroll) {
				timeoutTimer = setTimeout(scrollContent, 100)
			} else {
				setAutoStop(false)
				setAutoScroll(false)
			}
		}

		return () => {
			window.removeEventListener('scroll', onScroll)
			if (timeoutTimer) clearTimeout(timeoutTimer)
		}
		// `scrollTopPos` is deliberately omitted: this effect calls
		// `setScrollTopPos` on every tick, so depending on it would re-run the
		// effect and queue a second timer on each scroll step.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- see above
	}, [scrollTimerMilliSeconds, autoScroll, isScrollDivInView])

	return (
		<div
			className={`${className} px-2 pt-6 ${
				isAutoScroll ? '' : 'pb-6'
			} custom-scrollbar relative overflow-y-auto`}
			id={id}
			onFocus={pauseScroll}
			onKeyDown={(e) => {
				if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
					overwriteScroll()
				}
			}}
			onMouseOver={pauseScroll}
			onTouchMove={overwriteScroll}
			onWheel={overwriteScroll}
			role="presentation"
		>
			<div className="content">{children}</div>
			{isAutoScroll ? (
				<div className="sticky bottom-0 left-0 right-0 bg-white py-2 text-right">
					<EllipseButton
						as="button"
						icon={
							autoScroll ? (
								<svg version="1.1" viewBox="0 0 512 512">
									<g>
										<path d="M224,435.8V76.1c0-6.7-5.4-12.1-12.2-12.1h-71.6c-6.8,0-12.2,5.4-12.2,12.1v359.7c0,6.7,5.4,12.2,12.2,12.2h71.6   C218.6,448,224,442.6,224,435.8z" />
										<path d="M371.8,64h-71.6c-6.7,0-12.2,5.4-12.2,12.1v359.7c0,6.7,5.4,12.2,12.2,12.2h71.6c6.7,0,12.2-5.4,12.2-12.2V76.1   C384,69.4,378.6,64,371.8,64z" />
									</g>
								</svg>
							) : (
								ButtonIcons.Play
							)
						}
						onClick={(e) => {
							toggleScroll(e)
						}}
						size="sm"
					>
						{autoScroll ? 'Pause' : 'Play'}
					</EllipseButton>
				</div>
			) : null}
		</div>
	)
}
